import React, { useState } from "react";
import { createUseStyles } from "react-jss";
import GenericBasicInfo from "../../../../components/cards/GenericBasicInfo";
import {
  cardStyles,
  modalGenericStyles,
  variables,
} from "../../../../variables";
import PropTypes from "prop-types";
import _ from "lodash";
import { DateTime } from "luxon";
import { currencies } from "../../../../helpers";
import { useQuery } from "@tanstack/react-query";
import { retrieveRailEuropeAPIConfig } from "../../../../api/RailEuropeEngine";
import { genericRequestErrorHandler } from "../../../../api";
import Loader from "../../../../components/Loader";
import { RailEuropeTrueFalseMApping } from "../common";
import { CustomButton } from "../../../../components/CustomButton";
import TabStrip from "../../../../components/TabStrip";
import RailEuropeLogsTab from "./RailEuropeLogsTab";
import ReservationsStatusTag from "../../../../components/Tags/ReservationsStatusTag";

const styles = createUseStyles({
  ...modalGenericStyles,
  DetailsRailEuropeApiConfigModal: {
    ...modalGenericStyles.modal,
  },
  modalCard: {
    ...modalGenericStyles.card,
    gridTemplateRows: "max-content max-content 1fr max-content",
  },
  modalCardHeader: modalGenericStyles.cardHeader,
  modalCardBody: {
    ...modalGenericStyles.cardBody,
    display: "grid",
    gridGap: variables.normal_gap,
    gridTemplateRows: "repeat(2, max-content)",
  },
  modalCardActions: modalGenericStyles.cardActions,
  card: cardStyles.card,
  cardHeader: cardStyles.header,
  cardBody: cardStyles.body,
  row: {
    display: "grid",
    gridTemplateColumns: "repeat(1, 1fr)",
    gap: variables.normal_gap,
  },
  [`@media ${variables.media.smallscreen}`]: {
    row: {
      gridTemplateColumns: "2fr 1fr",
    },
  },
});
const DetailsRailEuropeApiConfigModal = ({ uid, onClose }) => {
  const classes = styles();

  const [activeTab, setActiveTab] = useState("basicData");

  const menuTabs = {
    basicData: "Basic Data",
    logs: "Logs",
  };

  const { data: retrievedRailEurope, isLoading: loading } = useQuery({
    queryKey: ["DetailRailEurope", uid],
    queryFn: async () => await retrieveRailEuropeAPIConfig({ uid: uid }),
    onError: (error) => genericRequestErrorHandler(error),
  });

  const basicDataCpl = [
    ["Uid", "uid"],
    ["Name", null, _.capitalize(_.get(retrievedRailEurope, "data.name"))],
    [
      "Supplier",
      null,
      _.capitalize(_.get(retrievedRailEurope, "data.supplier")),
    ],
    ["Source Entity", "source_entity"],
    ["Market Type", "market_type"],
    [
      "Currency",
      null,
      _.get(currencies, _.get(retrievedRailEurope, "data.currency")),
    ],
    [
      "Is Live",
      null,
      _.get(
        RailEuropeTrueFalseMApping,
        _.get(retrievedRailEurope, "data.is_live")
      ),
    ],
    [
      "Is Global",
      null,
      _.get(
        RailEuropeTrueFalseMApping,
        _.get(retrievedRailEurope, "data.global_account")
      ),
    ],
    [
      "Is Master Account",
      null,
      _.get(
        RailEuropeTrueFalseMApping,
        _.get(retrievedRailEurope, "data.is_master_account")
      ) || "N/A",
    ],
    ["Website Api Key", "website_api_key"],
    [
      "Status",
      null,
      <ReservationsStatusTag
        status={_.get(retrievedRailEurope, "data.status")}
      />,
    ],
  ];
  const creatorData = [
    [
      "Created",
      null,
      DateTime.fromISO(
        _.get(retrievedRailEurope, "data.created")
      ).toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY),
    ],
    ["Creator", "creator"],
  ];
  const editorData = [
    ["Last Editor", "last_editor"],
    [
      "Edited",
      null,
      DateTime.fromISO(
        _.get(retrievedRailEurope, "data.edited")
      ).toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY) || "N/A",
    ],
  ];
  const accessData = [
    [
      "Username",
      null,
      _.capitalize(_.get(retrievedRailEurope, "data.username")) || "N/A",
    ],
    ["Password", "password"],
    ["Application Key", "application_key"],
    ["Access Token", "access_token"],
    ["Api Key", "api_key"],
    ["Api Url", "api_url"],
  ];

  return (
    <div className={classes.DetailsRailEuropeApiConfigModal}>
      {/* {loading && <Loader onTop={true} />} */}
      <div className={classes.modalCard}>
        <div className={classes.modalCardHeader}>
          <span>Rail Europe API Configuration Details</span>
        </div>
        <TabStrip
          tabs={Object.entries(menuTabs)}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
        <div className={classes.modalCardBody}>
          {loading ? (
            <Loader />
          ) : (
            <React.Fragment>
              {activeTab === "basicData" && (
                <React.Fragment>
                  <div className={classes.row}>
                    <div className={classes.cardBody}>
                      <GenericBasicInfo
                        header={"Basic Data"}
                        cardInfo={basicDataCpl}
                        objectData={_.get(retrievedRailEurope, "data", {})}
                      />
                    </div>
                    <div className={classes.cardBody}>
                      <GenericBasicInfo
                        header={"Creator Data"}
                        cardInfo={creatorData}
                        objectData={_.get(retrievedRailEurope, "data", {})}
                      />
                      <GenericBasicInfo
                        header={"Editor Data"}
                        cardInfo={editorData}
                        objectData={_.get(retrievedRailEurope, "data", {})}
                      />
                    </div>
                  </div>
                  <div className={classes.row}>
                    <div className={classes.cardBody}>
                      <GenericBasicInfo
                        header="API Credentials"
                        cardInfo={accessData}
                        objectData={_.get(retrievedRailEurope, "data", {})}
                      />
                    </div>
                  </div>
                </React.Fragment>
              )}
              {activeTab === "logs" && <RailEuropeLogsTab />}
            </React.Fragment>
          )}
        </div>
        <div className={classes.cardActions}>
          <CustomButton appearance="ghost" text="Cancel" onClick={onClose}>
            <strong>Cancel</strong>
          </CustomButton>
        </div>
      </div>
    </div>
  );
};
DetailsRailEuropeApiConfigModal.propTypes = {
  uid: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};
export default DetailsRailEuropeApiConfigModal;
